import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { get } from "lodash";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import { useAppSelector } from "../../hooks/useSelector";
import facilityService from "../../services/facility";
import propertyService from "../../services/property";
import service from "../../services/room";
import queryDefault from "../../utils/queryDefault";

export default function RoomEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.global);
  const [property, setProperty] = useState({ id: 0, name: "" });
  const [loadingAddPhoto, setLoadingAddPhoto] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().min(2).required("Name is a required field and length > 2")
  });
  const form = useForm({
    defaultValues: {
      property_id: id,
      name: "",
      description: "",
      daily_price: 0,
      monthly_price: 0,
      half_yearly_price: 0,
      yearly_price: 0,
      admin_name: user.username,
      room_facilities: [],
      type: "male",
    },
    resolver: yupResolver(schema),
  });

  const updateRoom = useMutation(
    "update_Property",
    service.updateRoom,
    {
      onSuccess: () => {
        navigate(`/app/properties/property/detail/${property.id}`);

        notification.onOpen({
          message: "Room was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const listFacility = useQuery("list-facility", () => facilityService.getAllFacility({ keyword: "", type: "Room" }));

  const onSubmit = (values: any) => {
    const submittedValues = { ...values, id };
    updateRoom.mutate(submittedValues);
  };

  const currentRoom = useQuery("current-room", () =>
    service.getRoomById(Number(id))
  );

  useEffect(() => {
    if (currentRoom.isFetched) {
      form.setValue("property_id", get(currentRoom, "data.data.property_id"));
      form.setValue("name", get(currentRoom, "data.data.name"));
      form.setValue("description", get(currentRoom, "data.data.description"));
      form.setValue("daily_price", get(currentRoom, "data.data.daily_price"));
      form.setValue("monthly_price", get(currentRoom, "data.data.monthly_price"));
      form.setValue("half_yearly_price", get(currentRoom, "data.data.half_yearly_price"));
      form.setValue("yearly_price", get(currentRoom, "data.data.yearly_price"));
      form.setValue("type", get(currentRoom, "data.data.type"));
      form.setValue("room_facilities", get(currentRoom, "data.data.facilities")?.map((facility: any) => facility.facility_id));
      setSelectedElements(form.getValues("room_facilities"));
      propertyService.getPropertyById(get(currentRoom, "data.data.property_id")).then(response => {
        setProperty(response?.data?.property);
      })
    }
    // eslint-disable-next-line
  }, [currentRoom.isFetched]);

  const [selectedElements, setSelectedElements] = useState<any>([]);

  const onSelectionChange = (event: any) => {
    const value = event.target.value;
    form.setValue("room_facilities", value);
    setSelectedElements(value);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setLoadingAddPhoto(true);
      const formData = new FormData();
      formData.append("room", event.target.files[0]);
      formData.append("room_id", String(id));
      formData.append("admin_name", user.username);
      service.addPhoto(formData).then(() => {
        notification.onOpen({
          message: "Photo successfully added!",
          type: "success",
          position: "top",
        });
        currentRoom.refetch();
      }).catch(() => {
        notification.onOpen({
          message: "Failed add photo",
          type: "error",
          position: "top",
        });
      });
      setLoadingAddPhoto(false);
    }
  };

  return (
    <MainLayout>
      <Breadcrumb
        label={`Edit Property ${get(currentRoom, "data.data.name")}`}
        breadcrumbs={[
          { label: "Properties", href: "/app/properties/property" },
          {
            label: "Edit Property",
            href: `/app/properties/property/edit/${id}`,
          },
        ]}
      />

      <ProtectedPermissionPage
        menuSlug="property"
        requiredPermissions={{ is_create: true }}
      >
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Name</FormLabel>
                <Controller
                  name="name"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel required>Description</FormLabel>
                <Controller
                  name="description"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="medium"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                      >
                        <MenuItem value={"male"} key={0}>Male Only</MenuItem>
                        <MenuItem value={"female"} key={1}>Female Only</MenuItem>
                        <MenuItem value={"mix"} key={2}>Mix</MenuItem>
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Facility</FormLabel>
                <Controller
                  name="room_facilities"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <Box>
                      <Select
                        {...field}
                        multiple
                        size="small"
                        error={Boolean(fieldState.error?.message)}
                        fullWidth
                        renderValue={(selected: any) => selected.map((id: any) => {
                          var facility = ""
                          listFacility?.data?.data?.forEach((element: any) => {
                            if (element.id === id) {
                              facility = element.name
                            }
                          });
                          return facility;
                        }).join(", ")}
                        onChange={onSelectionChange}
                      >
                        {listFacility?.data?.data?.map((facility: any) => (
                          <MenuItem value={facility.id} key={facility.id}>
                            <ListItemText primary={facility.name} />
                            <Checkbox checked={selectedElements.indexOf(facility.id) > -1} />
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid item lg={6} xs={12}>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Daily Price</FormLabel>
                <Controller
                  name="daily_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Monthly Price</FormLabel>
                <Controller
                  name="monthly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Half-Yearly Price</FormLabel>
                <Controller
                  name="half_yearly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Yearly Price</FormLabel>
                <Controller
                  name="yearly_price"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      inputRef={ref}
                      error={Boolean(fieldState.error?.message)}
                      helperText={fieldState.error?.message}
                      size="small"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              variant="contained"
              onClick={form.handleSubmit(onSubmit)}
              loading={updateRoom.isLoading}
            >
              SAVE
            </LoadingButton>

            <Button variant="outlined" onClick={() => navigate(-1)}>
              CANCEL
            </Button>
          </Stack>
        </Box>

        {currentRoom.isFetched && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h3">Photos</Typography>
            {currentRoom?.data?.data?.photos.map((photo: any) => (
              <Box mt={2}>
                <Grid container spacing={2}>
                  <Grid lg={10} md={10}>
                    <img
                      src={process.env.REACT_APP_BACKEND_URL + photo.photo}
                      alt="Room"
                      style={{
                        width: "100%",
                        borderRadius: "0.375rem",
                        objectFit: "cover",
                        alignSelf: "start"
                      }}
                    />
                  </Grid>
                  <Grid lg={2} md={2}>
                    <Button variant="contained" sx={{ width: "100%" }}
                      onClick={() => { service.deletePhoto(photo.id).then(() => currentRoom.refetch()) }}>
                      Delete
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        <Box mt={3}>
          <input
            type="file"
            name="room"
            onChange={(event) =>
              handleFileChange(event)
            }
            hidden
          />
          <LoadingButton
            variant="contained"
            loading={loadingAddPhoto}
            component="span"
            onClick={() => {
              let element: HTMLElement = document.querySelector(
                'input[name="room"]'
              )!;
              element.click();
            }}
          >
            Upload Photo
          </LoadingButton>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
